import { useMemo } from 'react';
import type { FC } from 'react';
import { useAppleRequest } from '../../utils/api';
import { isHydrated } from '../../utils/utils';
import { APPLE_PAY_VERSION, ApplePay } from './ApplePay';
import type { Basket } from '../../bff-api/baskets/model';
import type { Product } from '../../bff-api/products/model';
import type { RequestParams, StepType } from './ApplePay.types';

export const shouldShowApplePay = (): boolean => {
    try {
        return (
            typeof ApplePaySession !== 'undefined' &&
            ApplePaySession.canMakePayments() &&
            ApplePaySession.supportsVersion(APPLE_PAY_VERSION) &&
            isHydrated()
        );
    } catch (err) {
        return false;
    }
};

interface ApplePayButtonProps {
    step: StepType;
    basket?: Basket;
    product?: Product;
    params?: RequestParams;
    isDisabled?: boolean;
    validationFn?: () => boolean | Promise<boolean>;
}

const ApplePayButton: FC<ApplePayButtonProps> = ({
    basket,
    product,
    step,
    params = {},
    isDisabled = false,
    validationFn = (): boolean | Promise<boolean> => true,
}) => {
    const showApplePay = useMemo(() => shouldShowApplePay(), []);

    const applePayRequest = useAppleRequest(
        showApplePay,
        params as Record<string, string | number | boolean>,
    );

    if (!showApplePay) {
        return null;
    }

    const applePayRequestData = applePayRequest.data?.request;
    const isRequestRefreshing = applePayRequest.isRefetching || applePayRequest.isFetching;

    const shouldDisableButton = isDisabled || isRequestRefreshing;

    return applePayRequestData ? (
        <ApplePay
            basket={basket}
            product={product}
            applePayRequest={applePayRequestData}
            step={step}
            params={params}
            isDisabled={shouldDisableButton}
            validationFn={validationFn}
        />
    ) : null;
};

ApplePayButton.defaultProps = {
    basket: undefined,
    product: undefined,
    params: undefined,
    isDisabled: false,
    validationFn: (): boolean | Promise<boolean> => true,
};

export { ApplePayButton };
